import dayjs from 'dayjs';
import { compareDates, getYear, scheduleIsEnded } from '../../lib/dayjs';
import { sliceToLimit, hasMoreThanLimit } from '../../lib/pagination';
import { getLastSchedule as getJourneyLastSchedule, IJourneyResp, ISchedules } from '../../services/journey.service';
import { getCurrentSchedule } from '../../components/shared/JourneyActionManager';

const TERMINAL_STATUSES = ['CANCELED', 'DONE', 'VOIDED'];
const FINALIZED_STATUSES = ['DONE', 'VOIDED'];

export type JourneyYearItem =
  | { kind: 'finalized'; journey: IJourneyResp; schedule: ISchedules }
  | { kind: 'pending'; journey: IJourneyResp };

export interface JourneyWithYears {
  journey: IJourneyResp;
  years: string[];
}

export default class JourneysModel {
  static isActiveJourney(journey: IJourneyResp): boolean {
    return !!getCurrentSchedule(journey) && !TERMINAL_STATUSES.includes(journey.status);
  }

  static isFinalizedJourney(journey: IJourneyResp): boolean {
    return FINALIZED_STATUSES.includes(journey.status);
  }

  static getLastSchedule(journey: IJourneyResp): ISchedules | undefined {
    return getJourneyLastSchedule(journey);
  }

  static getScheduleYears(journey: IJourneyResp): string[] {
    const years = (journey.schedules ?? [])
      .filter(schedule => schedule.dateStart)
      .map(schedule => getYear(dayjs(schedule.dateStart)));
    return Array.from(new Set(years));
  }

  static withScheduleYears(journeys: IJourneyResp[]): JourneyWithYears[] {
    return journeys.map(journey => ({ journey, years: JourneysModel.getScheduleYears(journey) }));
  }

  static extractYears(journeysWithYears: JourneyWithYears[]): string[] {
    const years = journeysWithYears.flatMap(item => item.years);
    return Array.from(new Set(years)).sort((a, b) => Number(a) - Number(b));
  }

  static filterByYear(journeysWithYears: JourneyWithYears[], year: string): IJourneyResp[] {
    return journeysWithYears.filter(item => item.years.includes(year)).map(item => item.journey);
  }

  static sortByLastSchedule(journeys: IJourneyResp[]): IJourneyResp[] {
    const finalized: IJourneyResp[] = [];
    const upcoming: { journey: IJourneyResp; dateEnd: string }[] = [];
    const awaitingAction: { journey: IJourneyResp; dateEnd: string }[] = [];

    journeys.forEach(journey => {
      if (JourneysModel.isFinalizedJourney(journey)) {
        finalized.push(journey);
        return;
      }
      const dateEnd = getCurrentSchedule(journey)?.dateEnd ?? '';
      if (dateEnd && !scheduleIsEnded(dateEnd)) {
        upcoming.push({ journey, dateEnd });
      } else {
        awaitingAction.push({ journey, dateEnd });
      }
    });

    upcoming.sort((a, b) => compareDates(a.dateEnd, b.dateEnd));
    awaitingAction.sort((a, b) => {
      if (!a.dateEnd) return 1;
      if (!b.dateEnd) return -1;
      return compareDates(b.dateEnd, a.dateEnd);
    });

    finalized.sort((a, b) => {
      const aDate = JourneysModel.getLastSchedule(a)?.dateEnd ?? '';
      const bDate = JourneysModel.getLastSchedule(b)?.dateEnd ?? '';
      if (!aDate) return 1;
      if (!bDate) return -1;
      return compareDates(bDate, aDate);
    });

    return [...upcoming.map(({ journey }) => journey), ...awaitingAction.map(({ journey }) => journey), ...finalized];
  }

  static getPreview<T>(items: T[], max: number): T[] {
    return sliceToLimit(items, max);
  }

  static hasMore(total: number, max: number): boolean {
    return hasMoreThanLimit(total, max);
  }

  static buildYearItems(journeys: IJourneyResp[]): JourneyYearItem[] {
    return journeys.reduce<JourneyYearItem[]>((items, journey) => {
      if (!JourneysModel.isFinalizedJourney(journey)) {
        items.push({ kind: 'pending', journey });
        return items;
      }
      const schedule = JourneysModel.getLastSchedule(journey);
      if (schedule) items.push({ kind: 'finalized', journey, schedule });
      return items;
    }, []);
  }
}
